/**
 * Serializes admin API calls that hit Worker Hyperdrive (max 1 conn per handler).
 * Rutas Hyperdrive (wishlist, barrios, geo-traffic) siempre pasan por la cola — nunca en paralelo.
 */

import { isHyperdriveAdminPath } from "@/lib/hyperdriveAdminPaths";

export type AdminRequestPriority = "critical" | "normal" | "low";

const PRIORITY_RANK: Record<AdminRequestPriority, number> = {
  critical: 0,
  normal: 1,
  low: 2,
};

/** Hyperdrive + Postgres: 1 request admin a la vez evita ERR_CONNECTION_RESET. */
const MAX_CONCURRENT = 1;
/** Breathing room entre requests (Hyperdrive / proxy Railway). */
const INTER_REQUEST_GAP_MS = 500;

type QueueItem<T> = {
  priority: AdminRequestPriority;
  run: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (reason: unknown) => void;
};

const queue: QueueItem<unknown>[] = [];
let active = 0;

function pump(): void {
  while (active < MAX_CONCURRENT && queue.length > 0) {
    queue.sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]);
    const item = queue.shift();
    if (!item) break;
    active += 1;
    void item
      .run()
      .then(item.resolve, item.reject)
      .finally(() => {
        active -= 1;
        setTimeout(pump, INTER_REQUEST_GAP_MS);
      });
  }
}

export function enqueueAdminRequest<T>(
  run: () => Promise<T>,
  priority: AdminRequestPriority = "normal",
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    queue.push({ priority, run, resolve, reject });
    pump();
  });
}

/** Descarta polls de tráfico en cola cuando un save crítico va directo. */
export function dropQueuedLowPriorityAdminRequests(): number {
  const before = queue.length;
  for (let i = queue.length - 1; i >= 0; i -= 1) {
    if (queue[i].priority === "low") {
      const item = queue[i];
      queue.splice(i, 1);
      item.reject(new Error("cancelled: critical save"));
    }
  }
  return before - queue.length;
}

/**
 * Bypass de cola solo para writes no-Hyperdrive (site-settings, premium-invite).
 * Hyperdrive paths: siempre serializados — paralelo = ERR_CONNECTION_RESET.
 */
export function shouldBypassAdminQueue(path: string, method = "GET"): boolean {
  const m = method.toUpperCase();
  // Lecturas y writes: Railway-first + sin cola Hyperdrive
  if (path.includes("/api/admin/site-settings")) return true;
  if (m === "GET" || m === "HEAD" || m === "OPTIONS") return false;
  if (isHyperdriveAdminPath(path)) return false;
  if (path.includes("/api/admin/premium-invite-config")) return true;
  return false;
}

/** Infer queue priority from admin API path + method. */
export function inferAdminRequestPriority(
  path: string,
  method = "GET",
): AdminRequestPriority {
  const m = method.toUpperCase();
  if (path.includes("/api/admin/geo-traffic-stats")) return "low";
  if (path.includes("/api/admin/geo-neighborhood-config")) return "critical";
  if (path.includes("/api/admin/wishlist-config")) return "critical";
  if (path.includes("/api/admin/analytics-report")) return "normal";
  if (path.includes("/api/admin/memberships")) return "normal";
  if (path.includes("/api/admin/mejores-amigos-config")) return "critical";
  if (path.includes("/api/admin/site-settings") && m !== "GET") return "critical";
  if (m === "POST" || m === "PUT" || m === "PATCH" || m === "DELETE") return "normal";
  if (path.startsWith("/api/admin/") || path.startsWith("/api/cloudinary/")) {
    return "critical";
  }
  return "normal";
}